import { useRef, useEffect, useCallback } from 'react';

/**
 * Snapshots the text of a pending selection at selection time, from the same
 * body the user selected against, and invalidates the selection if the body
 * changes underneath it.
 *
 * Resolving the text at code-click time instead raced with edit
 * broadcasts/refetches: a cleared body threw "Could not find text for
 * excerpt" (DELVE-FRONTEND-7), and a body that merely shifted would silently
 * splice the wrong text into the new excerpt.
 *
 * @param {Object|null} select - The current selection from CodeableContext
 * @param {string|null} selectedExcerptId - Set when an existing excerpt is
 *   selected; excerpt-backed selections have server-side text and need no snapshot
 * @param {string|undefined} body - The codeable's current text (reactive)
 * @param {Function} onDeselect - Called to clear a selection whose offsets no
 *   longer match the body
 * @returns {Function} getSnapshotText(selection) - The snapshotted text for
 *   that selection, or null if none was captured
 */
export const useSelectionTextSnapshot = (select, selectedExcerptId, body, onDeselect) => {
  const snapshotRef = useRef({ select: null, text: null, body: null });

  useEffect(() => {
    const snapshot = snapshotRef.current;

    if (!select || selectedExcerptId || select.start === undefined || select.end === undefined) {
      snapshotRef.current = { select: null, text: null, body: null };
      return;
    }

    if (snapshot.select !== select) {
      snapshotRef.current = {
        select,
        text: body ? body.substring(select.start, select.end) : null,
        body: body || null
      };
    } else if (snapshot.text === null && body) {
      // The body arrived after the selection was made.
      snapshotRef.current = {
        select,
        text: body.substring(select.start, select.end),
        body
      };
    } else if (snapshot.body !== body) {
      // The body changed under a pending selection, so its offsets now point
      // at different text — an excerpt created from them would land on the
      // wrong span. Drop the selection instead.
      snapshotRef.current = { select: null, text: null, body: null };
      onDeselect?.();
    }
  }, [select, selectedExcerptId, body, onDeselect]);

  const getSnapshotText = useCallback((selection) => {
    const snapshot = snapshotRef.current;
    return snapshot.select === selection ? snapshot.text : null;
  }, []);

  return getSnapshotText;
};
